import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { toast } from 'sonner';
import { QRDesignSettings, QRType } from '@/stores/qrStore';
import { Json } from '@/integrations/supabase/types';

export interface QRTemplate {
  id: string;
  name: string;
  category: string;
  description: string | null;
  qr_type: QRType;
  design_settings: Partial<QRDesignSettings>;
  thumbnail_url: string | null;
  is_premium: boolean;
  usage_count: number;
  created_at: string;
}

export interface UserTemplate {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  qr_type: QRType;
  design_settings: Partial<QRDesignSettings>;
  is_shared: boolean;
  created_at: string;
}

export const templateCategories = [
  'All',
  'Business',
  'Food & Hospitality',
  'Events',
  'Marketing',
  'E-commerce',
  'Education',
  'Health & Wellness',
  'Creative',
  'Real Estate',
  'Miscellaneous',
] as const;

export function useTemplates() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  // Fetch public templates
  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ['qr-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('qr_templates')
        .select('*')
        .order('usage_count', { ascending: false });

      if (error) throw error;
      
      return (data || []).map(t => ({
        ...t,
        qr_type: t.qr_type as QRType,
        design_settings: t.design_settings as Partial<QRDesignSettings>,
      })) as QRTemplate[];
    },
  });

  // Fetch user's custom templates
  const { data: userTemplates = [], isLoading: userTemplatesLoading } = useQuery({
    queryKey: ['user-templates', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('user_templates')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return (data || []).map(t => ({
        ...t,
        qr_type: t.qr_type as QRType,
        design_settings: t.design_settings as Partial<QRDesignSettings>,
      })) as UserTemplate[];
    },
    enabled: !!user,
  });

  // Increment template usage count
  const incrementUsage = useMutation({
    mutationFn: async (templateId: string) => {
      const template = templates.find(t => t.id === templateId);
      if (!template) return;
      
      const { error } = await supabase
        .from('qr_templates')
        .update({ usage_count: template.usage_count + 1 })
        .eq('id', templateId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr-templates'] });
    },
  });

  // Save custom template
  const saveUserTemplate = useMutation({
    mutationFn: async (params: {
      name: string;
      description?: string;
      qr_type: QRType;
      design_settings: Partial<QRDesignSettings>;
    }) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_templates')
        .insert({
          user_id: user.id,
          name: params.name,
          description: params.description || null,
          qr_type: params.qr_type,
          design_settings: params.design_settings as unknown as Json,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-templates'] });
      toast.success('Template saved!');
    },
    onError: (error) => {
      toast.error('Failed to save template: ' + error.message);
    },
  });

  // Delete user template
  const deleteUserTemplate = useMutation({
    mutationFn: async (templateId: string) => {
      const { error } = await supabase
        .from('user_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-templates'] });
      toast.success('Template deleted');
    },
  });

  return {
    templates,
    userTemplates,
    isLoading: templatesLoading || userTemplatesLoading,
    incrementUsage,
    saveUserTemplate,
    deleteUserTemplate,
  };
}
